const OBSERVER_RECHECK_INTERVAL_MS = 1000;

/**
 * Keeps React's DOM writes from throwing after an in-browser translator
 * (Chrome, Safari) has moved or replaced the nodes React rendered.
 * @param {object} [options]
 * @param {typeof Node} [options.NodeImpl] - The `Node` constructor to patch.
 * @param {(message: string) => void} [options.onMismatch] - Called once per kind of rewrite seen this session.
 */
export function installTranslationDomGuard({
  NodeImpl = typeof Node === "undefined" ? undefined : Node,
  onMismatch,
} = {}) {
  if (typeof NodeImpl !== "function" || NodeImpl.prototype.__stanzaTranslationGuard) return;
  const proto = NodeImpl.prototype;
  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;
  const reported = new Set();
  const reportOnce = (message) => {
    if (reported.has(message)) return;
    reported.add(message);
    console.warn(`[browserCompat] ${message}`);
    onMismatch?.(message);
  };

  proto.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      if (child.parentNode && this.contains(child)) {
        reportOnce("removeChild: node was wrapped, removed from its new parent");
        return child.parentNode.removeChild(child);
      }
      reportOnce("removeChild skipped: node was replaced");
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  proto.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Insert before the wrapper that now holds the reference; append if it was replaced.
      let anchor = referenceNode.parentNode;
      while (anchor && anchor.parentNode !== this) anchor = anchor.parentNode;
      reportOnce(`insertBefore: reference node was ${anchor ? "wrapped" : "replaced"}`);
      return originalInsertBefore.call(this, newNode, anchor ?? null);
    }
    return originalInsertBefore.apply(this, arguments);
  };

  proto.__stanzaTranslationGuard = true;
}

/**
 * Provides an `IntersectionObserver` against the viewport (honouring `rootMargin`
 * and `threshold`) where the browser has none. All observers share one throttled
 * scroll/resize check, paused while the page is hidden.
 * @param {Window} [win] - The window to install it on.
 */
export function installIntersectionObserverFallback(win = typeof window === "undefined" ? undefined : window) {
  if (!win || typeof win.IntersectionObserver === "function") return;

  const observers = new Set();
  const doc = win.document;
  const requestFrame = win.requestAnimationFrame
    ? win.requestAnimationFrame.bind(win)
    : (callback) => setTimeout(callback, 16);
  let frame = null;
  let interval = null;

  const schedule = () => {
    if (frame !== null || doc?.visibilityState === "hidden") return;
    frame = requestFrame(() => {
      frame = null;
      observers.forEach((observer) => observer.report());
    });
  };
  const start = () => {
    win.addEventListener("scroll", schedule, true);
    win.addEventListener("resize", schedule);
    doc?.addEventListener("visibilitychange", schedule);
    interval = setInterval(schedule, OBSERVER_RECHECK_INTERVAL_MS);
  };
  const stop = () => {
    win.removeEventListener("scroll", schedule, true);
    win.removeEventListener("resize", schedule);
    doc?.removeEventListener("visibilitychange", schedule);
    clearInterval(interval);
  };

  win.IntersectionObserver = class IntersectionObserver {
    constructor(callback, options) {
      const { rootMargin = "0px", threshold = 0 } = options ?? {};
      this.callback = callback;
      this.root = null;
      this.rootMargin = rootMargin;
      this.thresholds = [].concat(threshold).sort((a, b) => a - b);
      this.targets = new Map();
    }

    observe(target) {
      if (this.targets.has(target)) return;
      this.targets.set(target, undefined);
      if (!observers.size) start();
      observers.add(this);
      schedule();
    }

    unobserve(target) {
      this.targets.delete(target);
      if (!this.targets.size) this.disconnect();
    }

    disconnect() {
      this.targets.clear();
      if (observers.delete(this) && !observers.size) stop();
    }

    takeRecords() {
      return [];
    }

    /** Fires the callback for targets seen for the first time or that crossed a threshold. */
    report() {
      const rootBounds = getRootBounds(win, this.rootMargin);
      const time = win.performance?.now?.() ?? Date.now();
      const entries = [];
      for (const [target, previousIndex] of this.targets) {
        const rect = toRect(target.getBoundingClientRect());
        const overlaps =
          isRendered(target, rect) &&
          rect.bottom > rootBounds.top &&
          rect.right > rootBounds.left &&
          rect.top < rootBounds.bottom &&
          rect.left < rootBounds.right;
        const intersectionRect = overlaps ? intersectRects(rect, rootBounds) : toRect({});
        const area = rect.width * rect.height;
        const ratio = !overlaps ? 0 : area ? (intersectionRect.width * intersectionRect.height) / area : 1;
        const index = overlaps ? this.thresholds.filter((value) => ratio >= value).length : 0;
        if (index === previousIndex) continue;
        this.targets.set(target, index);
        entries.push({
          target,
          time,
          isIntersecting: index > 0,
          intersectionRatio: ratio,
          boundingClientRect: rect,
          intersectionRect,
          rootBounds,
        });
      }
      if (entries.length) this.callback(entries, this);
    }
  };
}

/**
 * Lets `scrollTo` / `scroll` / `scrollBy` accept an options object on browsers
 * that only take `(x, y)`, by retrying with coordinates when the call throws.
 * @param {Window} [win] - The window whose scroll methods are patched.
 */
export function installScrollOptionsFallback(win = typeof window === "undefined" ? undefined : window) {
  if (!win || win.__stanzaScrollFallback) return;

  const wrap = (target, method, isBy) => {
    const original = target?.[method];
    if (typeof original !== "function") return;
    target[method] = function scrollWithFallback(options, ...rest) {
      try {
        return original.call(this, options, ...rest);
      } catch (error) {
        if (!options || typeof options !== "object") throw error;
        const isWindow = this === win || this == null;
        const currentLeft = isBy ? 0 : isWindow ? win.pageXOffset : this.scrollLeft;
        const currentTop = isBy ? 0 : isWindow ? win.pageYOffset : this.scrollTop;
        return original.call(this, options.left ?? currentLeft, options.top ?? currentTop);
      }
    };
  };

  wrap(win, "scrollTo", false);
  wrap(win, "scroll", false);
  wrap(win, "scrollBy", true);
  const elementProto = win.Element?.prototype;
  if (elementProto) {
    const toOptions = (left, top) => (left && typeof left === "object" ? left : { left, top });
    if (typeof elementProto.scrollTo !== "function") {
      elementProto.scrollTo = function scrollTo(left, top) {
        const options = toOptions(left, top);
        if (options.left != null) this.scrollLeft = options.left;
        if (options.top != null) this.scrollTop = options.top;
      };
    }
    if (typeof elementProto.scroll !== "function") elementProto.scroll = elementProto.scrollTo;
    if (typeof elementProto.scrollBy !== "function") {
      elementProto.scrollBy = function scrollBy(left, top) {
        const options = toOptions(left, top);
        this.scrollLeft += options.left ?? 0;
        this.scrollTop += options.top ?? 0;
      };
    }
  }
  wrap(elementProto, "scrollTo", false);
  wrap(elementProto, "scroll", false);
  wrap(elementProto, "scrollBy", true);

  win.__stanzaScrollFallback = true;
}

/**
 * Viewport bounds grown (or shrunk) by a CSS-style `rootMargin` of px / % values.
 * @param {Window} win
 * @param {string} rootMargin - e.g. `"300px"` or `"-28% 0px -60% 0px"`.
 * @returns {DOMRectReadOnly}
 */
function getRootBounds(win, rootMargin) {
  const { innerWidth: width, innerHeight: height } = win;
  const values = String(rootMargin).trim().split(/\s+/);
  const [top, right = top, bottom = top, left = right] = values;
  const toPx = (value, size) => {
    const amount = parseFloat(value) || 0;
    return value.endsWith("%") ? (amount / 100) * size : amount;
  };
  return toRect({
    top: 0 - toPx(top, height),
    right: width + toPx(right, width),
    bottom: height + toPx(bottom, height),
    left: 0 - toPx(left, width),
  });
}

/**
 * The overlap of two rects.
 * @param {DOMRectReadOnly} a
 * @param {DOMRectReadOnly} b
 * @returns {DOMRectReadOnly}
 */
function intersectRects(a, b) {
  return toRect({
    top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
    left: Math.max(a.left, b.left),
  });
}

/**
 * Whether a target is in the document and has a layout box (not `display: none`).
 * @param {Element} target
 * @param {DOMRectReadOnly} rect - The target's bounding rect.
 * @returns {boolean}
 */
function isRendered(target, rect) {
  if (target.isConnected === false) return false;
  return rect.width > 0 || rect.height > 0 || target.getClientRects?.().length !== 0;
}

/**
 * A plain `DOMRectReadOnly`-shaped object from edge coordinates.
 * @param {{ top?: number, right?: number, bottom?: number, left?: number }} edges
 * @returns {DOMRectReadOnly}
 */
function toRect({ top = 0, right = 0, bottom = 0, left = 0 }) {
  return { top, right, bottom, left, x: left, y: top, width: right - left, height: bottom - top };
}
